import { Plus, X } from 'lucide-react';
import {
  useFieldArray,
  useFormContext,
  type ArrayPath,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { customValueTypes } from '../schemas/block-form-schema';

import { fieldText } from './field-text';

export interface CustomFieldsEditorProps<TForm extends FieldValues> {
  /** Path of the entries array inside the form, e.g. `custom` or `banner.items.0.custom`. */
  name: ArrayPath<TForm>;
  /** Names the group for assistive tech. */
  label: string;
}

/**
 * Key / type / value rows for the free `custom` bag the storefront receives verbatim. Typed
 * per row so "12" can travel as a number and "true" as a boolean; JSON covers the rest.
 */
export function CustomFieldsEditor<TForm extends FieldValues>({
  name,
  label,
}: CustomFieldsEditorProps<TForm>) {
  const { t } = useTranslation('content');
  const form = useFormContext<TForm>();
  const { fields, append, remove } = useFieldArray<TForm>({ control: form.control, name });
  const path = (index: number, field: 'key' | 'type' | 'value') =>
    `${name}.${index}.${field}` as Path<TForm>;

  return (
    <div className="flex flex-col gap-2" role="group" aria-label={label}>
      {fields.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('custom.empty')}</p>
      ) : null}
      {fields.map((row, index) => (
        <div
          key={row.id}
          className="grid grid-cols-[minmax(0,1fr)_120px_minmax(0,1.4fr)_auto] items-start gap-2"
        >
          <FormField
            control={form.control}
            name={path(index, 'key')}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    value={fieldText(field.value)}
                    placeholder={t('custom.key')}
                    aria-label={t('custom.keyOf', { index: index + 1 })}
                    className="font-mono text-[13px]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={path(index, 'type')}
            render={({ field }) => (
              <FormItem>
                <Select value={fieldText(field.value)} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger aria-label={t('custom.typeOf', { index: index + 1 })}>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {customValueTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {t(`custom.types.${type}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={path(index, 'value')}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    value={fieldText(field.value)}
                    placeholder={t('custom.value')}
                    aria-label={t('custom.valueOf', { index: index + 1 })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => remove(index)}
            aria-label={t('custom.remove', { index: index + 1 })}
          >
            <X aria-hidden />
          </Button>
        </div>
      ))}
      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            append({ key: '', type: 'string', value: '' } as Parameters<typeof append>[0])
          }
        >
          <Plus aria-hidden />
          {t('custom.add')}
        </Button>
      </div>
    </div>
  );
}
